/**
 * @file Duolingo-style mascot icons for badges
 * Each mascot is a simple flat-style SVG character (48x48 viewBox).
 */

interface SvgProps {
  size: number;
}

// ── Baby chick hatching from egg ──
function FirstStepsMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Egg shell bottom */}
      <path
        d="M10 30 C10 30 12 42 24 42 C36 42 38 30 38 30 L34 28 L14 28 Z"
        fill="#F3F4F6"
        stroke="#D1D5DB"
        strokeWidth="1"
      />
      <path
        d="M10 30 L14 28 L34 28 L38 30"
        fill="none"
        stroke="#D1D5DB"
        strokeWidth="1"
        strokeLinecap="round"
      />
      {/* Chick body */}
      <ellipse cx="24" cy="24" rx="10" ry="11" fill="#FBBF24" />
      {/* Egg shell top (cracked) */}
      <path
        d="M12 26 L14 18 L17 22 L20 14 L24 20 L28 14 L31 22 L34 18 L36 26"
        fill="#F3F4F6"
        stroke="#D1D5DB"
        strokeWidth="1"
        strokeLinejoin="round"
      />
      {/* Eyes */}
      <circle cx="20" cy="22" r="2" fill="#1F2937" />
      <circle cx="28" cy="22" r="2" fill="#1F2937" />
      <circle cx="21" cy="21" r="0.7" fill="white" />
      <circle cx="29" cy="21" r="0.7" fill="white" />
      {/* Beak */}
      <path d="M22 26 L24 29 L26 26" fill="#F97316" />
      {/* Blush */}
      <ellipse cx="17" cy="25" rx="2" ry="1" fill="#FDE68A" opacity="0.6" />
      <ellipse cx="31" cy="25" rx="2" ry="1" fill="#FDE68A" opacity="0.6" />
    </svg>
  );
}

// ── Clockwork owl ──
function ConsistentMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Body */}
      <ellipse cx="24" cy="28" rx="14" ry="16" fill="#5EEAD4" />
      {/* Belly */}
      <ellipse cx="24" cy="32" rx="8" ry="9" fill="#CCFBF1" />
      {/* Ear tufts */}
      <path d="M12 14 L16 20 L10 20 Z" fill="#2DD4BF" />
      <path d="M36 14 L38 20 L32 20 Z" fill="#2DD4BF" />
      {/* Eye circles */}
      <circle cx="18" cy="22" r="6" fill="white" stroke="#0D9488" strokeWidth="1.5" />
      <circle cx="30" cy="22" r="6" fill="white" stroke="#0D9488" strokeWidth="1.5" />
      {/* Clock face in left eye */}
      <circle cx="18" cy="22" r="3" fill="#1F2937" />
      <line
        x1="18"
        y1="22"
        x2="18"
        y2="20"
        stroke="#FBBF24"
        strokeWidth="1"
        strokeLinecap="round"
      />
      <line
        x1="18"
        y1="22"
        x2="20"
        y2="22"
        stroke="#FBBF24"
        strokeWidth="0.8"
        strokeLinecap="round"
      />
      {/* Right eye pupil */}
      <circle cx="30" cy="22" r="3" fill="#1F2937" />
      <circle cx="31" cy="21" r="1" fill="white" />
      {/* Beak */}
      <path d="M22 28 L24 32 L26 28" fill="#D97706" />
      {/* Feet */}
      <path
        d="M18 43 L16 46 M18 43 L18 46 M18 43 L20 46"
        stroke="#92400E"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M30 43 L28 46 M30 43 L30 46 M30 43 L32 46"
        stroke="#92400E"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Cat wearing a cape ──
function ActionHeroMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Cape */}
      <path
        d="M14 18 C8 24 6 40 10 44 L24 38 L38 44 C42 40 40 24 34 18 Z"
        fill="#DC2626"
        opacity="0.9"
      />
      {/* Body */}
      <ellipse cx="24" cy="30" rx="10" ry="12" fill="#9CA3AF" />
      {/* Head */}
      <circle cx="24" cy="18" r="10" fill="#9CA3AF" />
      {/* Ears */}
      <path d="M14 12 L17 6 L22 14 Z" fill="#9CA3AF" />
      <path d="M34 12 L31 6 L26 14 Z" fill="#9CA3AF" />
      <path d="M16 12 L18 8 L21 13 Z" fill="#F9A8D4" />
      <path d="M32 12 L30 8 L27 13 Z" fill="#F9A8D4" />
      {/* Eyes */}
      <ellipse cx="20" cy="17" rx="2.5" ry="3" fill="#7C3AED" />
      <ellipse cx="28" cy="17" rx="2.5" ry="3" fill="#7C3AED" />
      <ellipse cx="20" cy="17" rx="1.2" ry="2" fill="#1F2937" />
      <ellipse cx="28" cy="17" rx="1.2" ry="2" fill="#1F2937" />
      {/* Nose & mouth */}
      <ellipse cx="24" cy="21" rx="1.5" ry="1" fill="#F9A8D4" />
      <path d="M22 22 Q24 24 26 22" fill="none" stroke="#6B7280" strokeWidth="0.8" />
      {/* Whiskers */}
      <line x1="10" y1="19" x2="18" y2="20" stroke="#6B7280" strokeWidth="0.6" />
      <line x1="10" y1="22" x2="18" y2="22" stroke="#6B7280" strokeWidth="0.6" />
      <line x1="38" y1="19" x2="30" y2="20" stroke="#6B7280" strokeWidth="0.6" />
      <line x1="38" y1="22" x2="30" y2="22" stroke="#6B7280" strokeWidth="0.6" />
    </svg>
  );
}

// ── Beaver with clipboard ──
function TaskmasterMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Tail */}
      <ellipse cx="24" cy="44" rx="8" ry="3" fill="#92400E" />
      {/* Body */}
      <ellipse cx="24" cy="30" rx="12" ry="14" fill="#A16207" />
      {/* Belly */}
      <ellipse cx="24" cy="33" rx="7" ry="9" fill="#FDE68A" />
      {/* Head */}
      <circle cx="24" cy="16" r="10" fill="#A16207" />
      {/* Cheeks */}
      <ellipse cx="17" cy="19" rx="3" ry="2.5" fill="#CA8A04" />
      <ellipse cx="31" cy="19" rx="3" ry="2.5" fill="#CA8A04" />
      {/* Eyes */}
      <circle cx="20" cy="15" r="2.5" fill="white" />
      <circle cx="28" cy="15" r="2.5" fill="white" />
      <circle cx="20" cy="15" r="1.5" fill="#1F2937" />
      <circle cx="28" cy="15" r="1.5" fill="#1F2937" />
      {/* Nose */}
      <ellipse cx="24" cy="18" rx="2" ry="1.5" fill="#1F2937" />
      {/* Teeth */}
      <rect x="22" y="20" width="2" height="3" rx="0.5" fill="white" />
      <rect x="24.5" y="20" width="2" height="3" rx="0.5" fill="white" />
      {/* Clipboard */}
      <rect x="34" y="22" width="10" height="14" rx="1.5" fill="#F97316" />
      <rect x="36" y="20" width="6" height="3" rx="1" fill="#D97706" />
      <line x1="36" y1="28" x2="42" y2="28" stroke="white" strokeWidth="1" />
      <line x1="36" y1="31" x2="42" y2="31" stroke="white" strokeWidth="1" />
      <line x1="36" y1="34" x2="40" y2="34" stroke="white" strokeWidth="1" />
    </svg>
  );
}

// ── Fox with flame tail ──
function StreakStarterMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Flame tail */}
      <path d="M36 30 C42 26 44 18 40 14 C38 20 36 18 38 12 C34 18 32 22 36 30" fill="#EF4444" />
      <path d="M37 28 C41 24 42 20 40 16 C38 20 37 19 38 15 C36 19 35 22 37 28" fill="#F97316" />
      {/* Body */}
      <ellipse cx="22" cy="32" rx="12" ry="12" fill="#F97316" />
      {/* Belly */}
      <ellipse cx="22" cy="35" rx="7" ry="7" fill="#FEF3C7" />
      {/* Head */}
      <circle cx="22" cy="18" r="11" fill="#F97316" />
      {/* Ears */}
      <path d="M12 10 L15 4 L20 12 Z" fill="#F97316" />
      <path d="M32 10 L29 4 L24 12 Z" fill="#F97316" />
      <path d="M14 10 L16 6 L19 11 Z" fill="#FEF3C7" />
      <path d="M30 10 L28 6 L25 11 Z" fill="#FEF3C7" />
      {/* Face patch */}
      <path d="M16 14 Q22 20 28 14 Q22 24 16 14" fill="white" />
      {/* Eyes */}
      <circle cx="18" cy="17" r="2" fill="#1F2937" />
      <circle cx="26" cy="17" r="2" fill="#1F2937" />
      <circle cx="19" cy="16" r="0.7" fill="white" />
      <circle cx="27" cy="16" r="0.7" fill="white" />
      {/* Nose */}
      <ellipse cx="22" cy="20" rx="1.5" ry="1" fill="#1F2937" />
      {/* Mouth */}
      <path d="M20 21.5 Q22 23 24 21.5" fill="none" stroke="#1F2937" strokeWidth="0.6" />
    </svg>
  );
}

// ── Phoenix rising ──
function OnFireMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Flame wings */}
      <path d="M6 28 C2 20 8 10 14 14 C10 18 12 24 16 26" fill="#FBBF24" />
      <path d="M42 28 C46 20 40 10 34 14 C38 18 36 24 32 26" fill="#FBBF24" />
      <path d="M8 26 C6 20 10 14 14 16 C12 18 13 22 16 24" fill="#F97316" />
      <path d="M40 26 C42 20 38 14 34 16 C36 18 35 22 32 24" fill="#F97316" />
      {/* Body */}
      <ellipse cx="24" cy="30" rx="10" ry="12" fill="#EF4444" />
      {/* Belly */}
      <ellipse cx="24" cy="33" rx="6" ry="7" fill="#FBBF24" />
      {/* Head */}
      <circle cx="24" cy="18" r="9" fill="#EF4444" />
      {/* Crest feathers */}
      <path d="M20 10 C18 4 22 2 24 6" fill="#F97316" />
      <path d="M24 8 C24 2 28 2 28 8" fill="#FBBF24" />
      <path d="M27 10 C30 4 32 6 28 10" fill="#F97316" />
      {/* Eyes */}
      <circle cx="20" cy="17" r="2.5" fill="white" />
      <circle cx="28" cy="17" r="2.5" fill="white" />
      <circle cx="20" cy="17" r="1.5" fill="#1F2937" />
      <circle cx="28" cy="17" r="1.5" fill="#1F2937" />
      <circle cx="21" cy="16" r="0.6" fill="white" />
      <circle cx="29" cy="16" r="0.6" fill="white" />
      {/* Beak */}
      <path d="M22 21 L24 25 L26 21" fill="#D97706" />
    </svg>
  );
}

// ── Wise owl with graduation cap ──
function MentorMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Body */}
      <ellipse cx="24" cy="32" rx="13" ry="14" fill="#1E3A5F" />
      {/* Belly */}
      <ellipse cx="24" cy="35" rx="8" ry="9" fill="#93C5FD" />
      {/* Wings */}
      <path d="M8 28 C6 34 8 42 12 44 L14 32" fill="#1E3A5F" />
      <path d="M40 28 C42 34 40 42 36 44 L34 32" fill="#1E3A5F" />
      {/* Head */}
      <circle cx="24" cy="18" r="11" fill="#1E3A5F" />
      {/* Eye circles */}
      <circle cx="19" cy="18" r="5" fill="white" />
      <circle cx="29" cy="18" r="5" fill="white" />
      <circle cx="19" cy="18" r="2.5" fill="#1F2937" />
      <circle cx="29" cy="18" r="2.5" fill="#1F2937" />
      <circle cx="20" cy="17" r="1" fill="white" />
      <circle cx="30" cy="17" r="1" fill="white" />
      {/* Beak */}
      <path d="M22 22 L24 26 L26 22" fill="#D97706" />
      {/* Grad cap */}
      <rect x="13" y="7" width="22" height="3" rx="0.5" fill="#1F2937" />
      <polygon points="24,4 34,9 24,11 14,9" fill="#1F2937" />
      <line x1="34" y1="9" x2="36" y2="16" stroke="#FBBF24" strokeWidth="1" />
      <circle cx="36" cy="16.5" r="1.5" fill="#FBBF24" />
      {/* Feet */}
      <path
        d="M19 45 L17 48 M19 45 L19 48 M19 45 L21 48"
        stroke="#D97706"
        strokeWidth="1"
        strokeLinecap="round"
      />
      <path
        d="M29 45 L27 48 M29 45 L29 48 M29 45 L31 48"
        stroke="#D97706"
        strokeWidth="1"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Raccoon on a rocket ──
function RisingStarMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Rocket flame */}
      <path d="M20 44 C22 40 24 42 24 46 C24 42 26 40 28 44" fill="#F97316" />
      <path d="M21 43 C23 40 24 41 24 44 C24 41 25 40 27 43" fill="#FBBF24" />
      {/* Rocket body */}
      <path
        d="M18 26 L18 40 C18 42 30 42 30 40 L30 26 C30 20 24 12 24 12 C24 12 18 20 18 26"
        fill="#3B82F6"
      />
      <path d="M24 12 C24 12 20 18 19 24 L29 24 C28 18 24 12 24 12" fill="#60A5FA" />
      {/* Rocket window */}
      <circle cx="24" cy="28" r="4" fill="#BFDBFE" stroke="#2563EB" strokeWidth="1" />
      {/* Rocket fins */}
      <path d="M18 36 L12 42 L18 40" fill="#EF4444" />
      <path d="M30 36 L36 42 L30 40" fill="#EF4444" />
      {/* Raccoon head (peeking from top) */}
      <circle cx="24" cy="14" r="7" fill="#6B7280" />
      {/* Raccoon mask */}
      <path
        d="M17 12 C19 10 21 12 24 12 C27 12 29 10 31 12 C29 14 27 14 24 14 C21 14 19 14 17 12"
        fill="#1F2937"
      />
      {/* Eyes in mask */}
      <circle cx="20" cy="12" r="2" fill="white" />
      <circle cx="28" cy="12" r="2" fill="white" />
      <circle cx="20" cy="12" r="1" fill="#1F2937" />
      <circle cx="28" cy="12" r="1" fill="#1F2937" />
      {/* Nose */}
      <ellipse cx="24" cy="15" rx="1.5" ry="1" fill="#1F2937" />
      {/* Ears */}
      <circle cx="18" cy="8" r="2.5" fill="#6B7280" />
      <circle cx="30" cy="8" r="2.5" fill="#6B7280" />
      <circle cx="18" cy="8" r="1.2" fill="#F9A8D4" />
      <circle cx="30" cy="8" r="1.2" fill="#F9A8D4" />
    </svg>
  );
}

// ── Mountain goat on peak ──
function LevelUpMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Mountain */}
      <path d="M4 46 L24 16 L44 46 Z" fill="#16A34A" />
      <path d="M4 46 L24 16 L20 46 Z" fill="#15803D" />
      {/* Snow cap */}
      <path d="M20 22 L24 16 L28 22 L26 20 L24 22 L22 20 Z" fill="white" />
      {/* Goat body */}
      <ellipse cx="24" cy="18" rx="6" ry="5" fill="white" />
      {/* Goat head */}
      <circle cx="24" cy="11" r="5" fill="white" />
      {/* Horns */}
      <path
        d="M20 8 C18 4 16 4 16 6"
        stroke="#92400E"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M28 8 C30 4 32 4 32 6"
        stroke="#92400E"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
      {/* Eyes */}
      <circle cx="22" cy="10" r="1.5" fill="#1F2937" />
      <circle cx="26" cy="10" r="1.5" fill="#1F2937" />
      {/* Nose */}
      <ellipse cx="24" cy="13" rx="1" ry="0.7" fill="#F9A8D4" />
      {/* Beard */}
      <path d="M23 14 C23 17 22 18 22 18" stroke="#D1D5DB" strokeWidth="1" strokeLinecap="round" />
      <path d="M25 14 C25 17 26 18 26 18" stroke="#D1D5DB" strokeWidth="1" strokeLinecap="round" />
      {/* Legs */}
      <line
        x1="20"
        y1="22"
        x2="19"
        y2="26"
        stroke="#D1D5DB"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="28"
        y1="22"
        x2="29"
        y2="26"
        stroke="#D1D5DB"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      {/* Flag */}
      <line x1="30" y1="6" x2="30" y2="16" stroke="#92400E" strokeWidth="1" />
      <path d="M30 6 L38 8 L30 10" fill="#EF4444" />
    </svg>
  );
}

// ── Two penguins high-fiving ──
function TeamPlayerMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Left penguin body */}
      <ellipse cx="16" cy="30" rx="8" ry="12" fill="#1F2937" />
      <ellipse cx="16" cy="33" rx="5" ry="7" fill="white" />
      {/* Left penguin head */}
      <circle cx="16" cy="18" r="7" fill="#1F2937" />
      <circle cx="14" cy="17" r="1.5" fill="white" />
      <circle cx="18" cy="17" r="1.5" fill="white" />
      <circle cx="14" cy="17" r="0.8" fill="#1F2937" />
      <circle cx="18" cy="17" r="0.8" fill="#1F2937" />
      <path d="M15 20 L16 22 L17 20" fill="#F97316" />
      {/* Right penguin body */}
      <ellipse cx="32" cy="30" rx="8" ry="12" fill="#1F2937" />
      <ellipse cx="32" cy="33" rx="5" ry="7" fill="white" />
      {/* Right penguin head */}
      <circle cx="32" cy="18" r="7" fill="#1F2937" />
      <circle cx="30" cy="17" r="1.5" fill="white" />
      <circle cx="34" cy="17" r="1.5" fill="white" />
      <circle cx="30" cy="17" r="0.8" fill="#1F2937" />
      <circle cx="34" cy="17" r="0.8" fill="#1F2937" />
      <path d="M31 20 L32 22 L33 20" fill="#F97316" />
      {/* High-five flippers */}
      <path
        d="M22 20 C23 18 24 16 24 14"
        stroke="#1F2937"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M26 20 C25 18 24 16 24 14"
        stroke="#1F2937"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      {/* Sparkle at high-five point */}
      <path
        d="M24 12 L24 10 M22 12 L21 11 M26 12 L27 11"
        stroke="#3B82F6"
        strokeWidth="1"
        strokeLinecap="round"
      />
      {/* Feet */}
      <ellipse cx="14" cy="42" rx="3" ry="1.5" fill="#F97316" />
      <ellipse cx="18" cy="42" rx="3" ry="1.5" fill="#F97316" />
      <ellipse cx="30" cy="42" rx="3" ry="1.5" fill="#F97316" />
      <ellipse cx="34" cy="42" rx="3" ry="1.5" fill="#F97316" />
    </svg>
  );
}

// ── Parrot with speech bubble ──
function FeedbackChampionMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Speech bubble */}
      <rect
        x="28"
        y="2"
        width="18"
        height="12"
        rx="3"
        fill="white"
        stroke="#D1D5DB"
        strokeWidth="1"
      />
      <path d="M30 14 L28 18 L34 14" fill="white" stroke="#D1D5DB" strokeWidth="1" />
      <line
        x1="32"
        y1="6"
        x2="42"
        y2="6"
        stroke="#22C55E"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="32"
        y1="9"
        x2="39"
        y2="9"
        stroke="#86EFAC"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      {/* Tail feathers */}
      <path d="M12 36 C6 40 4 44 6 46 L14 40" fill="#EF4444" />
      <path d="M10 34 C4 36 2 40 4 44 L12 38" fill="#22C55E" />
      <path d="M14 36 C8 42 8 46 10 46 L16 40" fill="#FBBF24" />
      {/* Body */}
      <ellipse cx="22" cy="32" rx="10" ry="12" fill="#22C55E" />
      {/* Belly */}
      <ellipse cx="22" cy="35" rx="6" ry="7" fill="#DCFCE7" />
      {/* Wing */}
      <path d="M30 26 C34 28 36 34 32 38 L28 30" fill="#16A34A" />
      {/* Head */}
      <circle cx="22" cy="18" r="9" fill="#22C55E" />
      {/* Eye ring */}
      <circle cx="20" cy="16" r="4" fill="white" />
      <circle cx="20" cy="16" r="2" fill="#1F2937" />
      <circle cx="21" cy="15" r="0.7" fill="white" />
      {/* Beak */}
      <path d="M26 16 C30 16 30 20 28 22 L26 20 Z" fill="#FBBF24" />
      <line x1="26" y1="19" x2="29" y2="19" stroke="#D97706" strokeWidth="0.5" />
      {/* Head feathers */}
      <path d="M18 10 C16 6 20 4 22 8" fill="#EF4444" />
      <path d="M22 10 C22 6 26 4 26 8" fill="#FBBF24" />
      {/* Feet */}
      <path
        d="M18 43 L16 46 M18 43 L18 46 M18 43 L20 46"
        stroke="#D97706"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M26 43 L24 46 M26 43 L26 46 M26 43 L28 46"
        stroke="#D97706"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Robin on sunrise branch ──
function EarlyBirdMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Sun rays */}
      <circle cx="38" cy="10" r="6" fill="#FBBF24" opacity="0.3" />
      <circle cx="38" cy="10" r="3" fill="#FBBF24" />
      <path
        d="M38 2 L38 4 M44 4 L42 6 M46 10 L44 10 M44 16 L42 14 M38 18 L38 16"
        stroke="#FBBF24"
        strokeWidth="1"
        strokeLinecap="round"
      />
      {/* Branch */}
      <path
        d="M2 38 C10 36 20 34 36 32 C40 32 42 34 44 34"
        stroke="#92400E"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M30 32 C32 28 36 28 36 28"
        stroke="#92400E"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      {/* Tail */}
      <path d="M12 28 C8 24 6 22 8 20" stroke="#92400E" strokeWidth="2" strokeLinecap="round" />
      <path d="M14 28 C10 24 10 20 12 18" stroke="#A16207" strokeWidth="2" strokeLinecap="round" />
      {/* Body */}
      <ellipse cx="20" cy="26" rx="9" ry="8" fill="#92400E" />
      {/* Red breast */}
      <ellipse cx="22" cy="28" rx="5" ry="5" fill="#F97316" />
      {/* Head */}
      <circle cx="26" cy="18" r="7" fill="#92400E" />
      {/* Eye */}
      <circle cx="28" cy="17" r="2" fill="white" />
      <circle cx="28.5" cy="16.5" r="1" fill="#1F2937" />
      {/* Beak */}
      <path d="M32 18 L38 17 L32 20 Z" fill="#D97706" />
      {/* Feet on branch */}
      <path
        d="M16 34 L16 38 M18 34 L18 38 M22 34 L22 38 M24 34 L24 38"
        stroke="#D97706"
        strokeWidth="1"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Cat with monocle ──
function PerfectionistMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Body */}
      <ellipse cx="24" cy="32" rx="11" ry="13" fill="#6B7280" />
      {/* Belly */}
      <ellipse cx="24" cy="35" rx="6" ry="8" fill="#D1D5DB" />
      {/* Head */}
      <circle cx="24" cy="18" r="11" fill="#6B7280" />
      {/* Ears */}
      <path d="M14 10 L17 4 L22 12 Z" fill="#6B7280" />
      <path d="M34 10 L31 4 L26 12 Z" fill="#6B7280" />
      <path d="M16 10 L18 6 L21 11 Z" fill="#F9A8D4" />
      <path d="M32 10 L30 6 L27 11 Z" fill="#F9A8D4" />
      {/* Left eye (normal) */}
      <circle cx="19" cy="17" r="3" fill="white" />
      <circle cx="19" cy="17" r="1.8" fill="#1F2937" />
      <circle cx="20" cy="16" r="0.6" fill="white" />
      {/* Right eye (monocle) */}
      <circle cx="29" cy="17" r="4" fill="none" stroke="#FBBF24" strokeWidth="1.5" />
      <circle cx="29" cy="17" r="3" fill="white" />
      <circle cx="29" cy="17" r="1.8" fill="#1F2937" />
      <circle cx="30" cy="16" r="0.6" fill="white" />
      {/* Monocle chain */}
      <path d="M33 19 C34 22 36 24 38 26" stroke="#FBBF24" strokeWidth="0.8" fill="none" />
      {/* Nose */}
      <ellipse cx="24" cy="21" rx="1.5" ry="1" fill="#F9A8D4" />
      {/* Mouth (smug smile) */}
      <path d="M21 23 Q24 25 27 23" fill="none" stroke="#4B5563" strokeWidth="0.8" />
      {/* Whiskers */}
      <line x1="10" y1="19" x2="17" y2="20" stroke="#9CA3AF" strokeWidth="0.5" />
      <line x1="10" y1="22" x2="17" y2="22" stroke="#9CA3AF" strokeWidth="0.5" />
      <line x1="38" y1="19" x2="31" y2="20" stroke="#9CA3AF" strokeWidth="0.5" />
      <line x1="38" y1="22" x2="31" y2="22" stroke="#9CA3AF" strokeWidth="0.5" />
      {/* Tail */}
      <path d="M34 38 C38 36 40 32 42 34 C40 36 38 40 34 42" fill="#6B7280" />
    </svg>
  );
}

// ── Sprouting plant character ──
function GrowthMindsetMascot({ size }: SvgProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Pot */}
      <path d="M14 34 L16 46 L32 46 L34 34 Z" fill="#92400E" />
      <rect x="12" y="32" width="24" height="4" rx="1" fill="#A16207" />
      {/* Stem */}
      <path
        d="M24 32 C24 24 24 20 24 16"
        stroke="#16A34A"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      {/* Left leaf */}
      <path d="M24 26 C18 22 14 24 14 28 C14 30 18 30 24 26" fill="#22C55E" />
      <line x1="24" y1="26" x2="16" y2="27" stroke="#16A34A" strokeWidth="0.5" />
      {/* Right leaf */}
      <path d="M24 22 C30 18 34 20 34 24 C34 26 30 26 24 22" fill="#22C55E" />
      <line x1="24" y1="22" x2="32" y2="23" stroke="#16A34A" strokeWidth="0.5" />
      {/* Top sprout leaves */}
      <path d="M24 16 C20 10 18 8 16 10 C18 12 20 14 24 16" fill="#4ADE80" />
      <path d="M24 16 C28 10 30 8 32 10 C30 12 28 14 24 16" fill="#4ADE80" />
      {/* Face on pot */}
      <circle cx="20" cy="40" r="1.5" fill="#1F2937" />
      <circle cx="28" cy="40" r="1.5" fill="#1F2937" />
      <path
        d="M22 43 Q24 45 26 43"
        fill="none"
        stroke="#1F2937"
        strokeWidth="1"
        strokeLinecap="round"
      />
      {/* Blush */}
      <ellipse cx="17" cy="42" rx="2" ry="1" fill="#FBBF24" opacity="0.4" />
      <ellipse cx="31" cy="42" rx="2" ry="1" fill="#FBBF24" opacity="0.4" />
      {/* Sparkle */}
      <path d="M38 8 L39 6 L40 8 L42 9 L40 10 L39 12 L38 10 L36 9 Z" fill="#FBBF24" />
    </svg>
  );
}

// ── Slug → Component mapping ──

const MASCOT_MAP: Record<string, (props: SvgProps) => React.JSX.Element> = {
  'first-steps': FirstStepsMascot,
  consistent: ConsistentMascot,
  'action-hero': ActionHeroMascot,
  taskmaster: TaskmasterMascot,
  'streak-starter': StreakStarterMascot,
  'on-fire': OnFireMascot,
  mentor: MentorMascot,
  'rising-star': RisingStarMascot,
  'level-up': LevelUpMascot,
  'team-player': TeamPlayerMascot,
  'feedback-champion': FeedbackChampionMascot,
  'early-bird': EarlyBirdMascot,
  perfectionist: PerfectionistMascot,
  'growth-mindset': GrowthMindsetMascot,
};

/** Map XP level titles to their mascot avatar slugs */
export const LEVEL_MASCOT: Record<string, string> = {
  Newcomer: 'first-steps',
  Contributor: 'rising-star',
  Achiever: 'action-hero',
  Champion: 'level-up',
  Legend: 'mentor',
};

/**
 * One plain line shown next to a level title (Newcomer, Contributor, ...) so a
 * new hire knows what the word means. The titles themselves stay as they are.
 */
export const LEVEL_EXPLAINER = 'Your level in Harmny points';

/**
 * One plain line explaining "Spark", the points currency, shown (as a subtitle
 * or tooltip) the first place a user meets the word on a screen. The currency
 * keeps its name; this only says what it is.
 */
export const SPARK_EXPLAINER =
  'Spark: the Harmny points you earn for growth activity, like goals, feedback, and 1:1s.';

// ── MascotIcon wrapper ──

interface MascotIconProps {
  slug: string;
  earned: boolean;
  size?: number;
  className?: string;
}

export function MascotIcon({ slug, earned, size = 48, className = '' }: MascotIconProps) {
  const MascotComponent = MASCOT_MAP[slug];

  if (!MascotComponent) {
    // Fallback: render a "?" circle for unknown slugs
    return (
      <div
        className={`flex items-center justify-center rounded-full bg-gray-200 text-gray-400 ${className}`}
        style={{ width: size, height: size, fontSize: size * 0.4 }}
      >
        ?
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center justify-center ${
        earned ? 'transition-transform hover:scale-110' : 'opacity-50 grayscale'
      } ${className}`}
    >
      <MascotComponent size={size} />
    </div>
  );
}
